/**
 * Baker Tilly International's evaluation of Baker Tilly Canada as one entity (Andres, 2026-09-24): how BTI rated
 * the July 2025 Wave 1 submission, and each item beside what the network's CDW results show today. Verdicts are
 * shared content; National's responses and BTI's comments come only from GET /national/bti (National IT).
 * Informative, not a verdict on anyone: "reported July 2025" beside "measured 2026".
 *
 * National's current position (Andres, 2026-09-25): where National IT believes an item is met today, it records
 * its position and explanation. It is shown beside BTI's verdict, clearly labelled as National's, and never
 * changes BTI's rating or the summary. National IT updates it on the National view; National and the Board see
 * it read-only on Leadership; firm members never get it.
 */
import {
  BTI_DOMAINS,
  BTI_POSITION,
  BTI_POSITION_EXPLANATION_MIN,
  BTI_POSITION_VALUES,
  BTI_VERDICT,
  BTI_WAVE1_EVALUATION,
  BTI_WAVE2_DUE,
  btiVerdictCounts,
  type BtiItem,
  type BtiPosition,
  type BtiPositionValue,
  type BtiVerdict,
} from '@grc/shared/vocabulary';
import { useState, type FormEvent, type ReactNode } from 'react';
import { ApiError } from '../api';
import { Ring } from '../charts';
import { formatDate } from '../components';

const ORDER: BtiVerdict[] = ['satisfied', 'partially_satisfied', 'not_satisfied', 'not_assessed'];
const items = BTI_WAVE1_EVALUATION.items;

export interface Measured {
  controlId: string;
  rated: number;
  /** Firms with it in place, fully or partly (the portal-wide rule since 2026-09-26). */
  inPlace: number;
  /** Of inPlace, the firms with it only partly in place. */
  partly: number;
}

export interface BtiPositionInput {
  position: BtiPositionValue;
  explanation: string;
  evidenceNote: string | null;
  version?: string;
}

/** National IT only: saves National's position on one item and resolves with what was stored. */
export type SavePosition = (itemNo: number, input: BtiPositionInput) => Promise<BtiPosition>;

type Comments = Record<number, { response: string | null; evaluatorComment: string | null }>;

export function daysUntil(iso: string, today = new Date()) {
  const d = new Date(`${iso}T12:00:00`);
  return Math.ceil((d.getTime() - today.getTime()) / 86_400_000);
}

/** BTI's verdicts: a ring for satisfied of 25, and one bar per BTI area. National's positions add one line, never change the figures. */
export function BtiSummary({ positions, showDue = true }: { positions?: BtiPosition[]; showDue?: boolean }) {
  const counts = btiVerdictCounts();
  const days = daysUntil(BTI_WAVE2_DUE);
  const met = positions?.filter((p) => p.position === 'met').length ?? 0;
  return (
    <div className="bti-summary">
      <div className="bti-ring">
        <Ring size={112} pct={(counts.satisfied / items.length) * 100} median={null} text={`${counts.satisfied}/${items.length}`} label={`BTI rated ${counts.satisfied} of ${items.length} Wave 1 requirements satisfied`} />
        <ul className="plain bti-legend small">
          {ORDER.filter((v) => counts[v] > 0).map((v) => (
            <li key={v}>
              <span className={`bti-key bti-${v}`} aria-hidden="true" /> <strong>{counts[v]}</strong> {BTI_VERDICT[v].label.toLowerCase()}
            </li>
          ))}
        </ul>
      </div>
      <ul className="plain bti-domains" aria-label="BTI verdicts by area">
        {BTI_DOMAINS.map((d) => {
          const inArea = items.filter((i) => i.domain === d);
          const c = btiVerdictCounts(inArea);
          return (
            <li key={d}>
              <span className="bti-domain-name">{d}</span>
              <span className="bti-bar" role="img" aria-label={`${d}: ${ORDER.filter((v) => c[v]).map((v) => `${c[v]} ${BTI_VERDICT[v].label.toLowerCase()}`).join(', ')}`}>
                {ORDER.filter((v) => c[v] > 0).map((v) => (
                  <span key={v} className={`seg bti-${v}`} style={{ width: `${(c[v] / inArea.length) * 100}%` }} />
                ))}
              </span>
              <span className="small">
                {c.satisfied} of {inArea.length} satisfied
              </span>
            </li>
          );
        })}
      </ul>
      {positions && positions.length > 0 && (
        <p className="small bti-position-summary">
          <strong>National IT&rsquo;s position:</strong> {met} of {items.length} met.
        </p>
      )}
      {showDue && <p className="small muted bti-due">
        Wave 2 is due to BTI on <strong>{formatDate(BTI_WAVE2_DUE)}</strong>
        {days > 0 && <> ({days} days)</>}.
      </p>}
    </div>
  );
}

/** Today's measure as a sentence, used to prefill the evidence note. */
export const measureNote = (x: Measured | undefined) =>
  x && x.rated > 0 ? `${x.inPlace} of ${x.rated} firms with CDW results have it in place, ${x.partly} of them partly (${formatDate(new Date().toISOString().slice(0, 10))})` : '';

/** How an item is named to screen readers: "item 20" for Wave 1, "Wave 2 item 8" for Wave 2, so the two never share a name on one page. */
export const itemName = (wave: 1 | 2, no: number) => (wave === 1 ? `requirement ${no}` : `Wave 2 requirement ${no}`);

/** National's position as a dashed chip; opens the explanation. Shared by Wave 1 and Wave 2. */
export function PositionChip({ p, expanded, onToggle }: { p: BtiPosition; expanded: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={`chip bti-national tone-${BTI_POSITION[p.position].tone}`}
      title={`National IT's current position: ${BTI_POSITION[p.position].label}. ${p.explanation}`}
      aria-expanded={expanded}
      onClick={onToggle}
    >
      National IT: {BTI_POSITION[p.position].label}
      <span className="sr-only">, National IT&rsquo;s position on {itemName(p.wave, p.itemNo)}; show the explanation</span>
    </button>
  );
}

/** The explanation behind National's position, with the evidence and who set it when. */
export function PositionDetail({ p, footnote }: { p: BtiPosition; footnote: ReactNode }) {
  return (
    <div className="bti-position small" role="region" aria-label={`National IT's position on ${itemName(p.wave, p.itemNo)}`}>
      <p>
        <strong>National IT&rsquo;s current position: {BTI_POSITION[p.position].label}.</strong> {p.explanation}
      </p>
      {p.evidenceNote && (
        <p>
          <strong>Evidence:</strong> {p.evidenceNote}
        </p>
      )}
      <p className="muted">
        Updated by {p.updatedByName} on {formatDate(p.updatedAt)}. {footnote}
      </p>
    </div>
  );
}

/** National IT's form for its position on one item, either wave. */
export function PositionForm({ wave, itemNo, title, intro, current, measured, onSave, onDone }: {
  wave: 1 | 2;
  itemNo: number;
  title: string;
  /** One line above the form: what the position is, beside what. */
  intro: ReactNode;
  current: BtiPosition | undefined;
  measured: Measured | undefined;
  onSave: SavePosition;
  onDone: (saved: BtiPosition | null) => void;
}) {
  const [position, setPosition] = useState<BtiPositionValue>(current?.position ?? 'met');
  const [explanation, setExplanation] = useState(current?.explanation ?? '');
  const [evidenceNote, setEvidenceNote] = useState(current ? (current.evidenceNote ?? '') : measureNote(measured));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const id = wave === 1 ? `bti-pos-${itemNo}` : `bti-pos2-${itemNo}`;
  const short = explanation.trim().length < BTI_POSITION_EXPLANATION_MIN;
  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (short) {
      setError(`Please explain in at least ${BTI_POSITION_EXPLANATION_MIN} characters.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const saved = await onSave(itemNo, { position, explanation: explanation.trim(), evidenceNote: evidenceNote.trim() || null, ...(current ? { version: current.version } : {}) });
      onDone(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That change did not save.');
      setBusy(false);
    }
  };
  return (
    <form className="form bti-position-form" onSubmit={save} noValidate aria-label={`National IT's position on ${itemName(wave, itemNo)}, ${title}`}>
      <p className="small muted">{intro}</p>
      <div className="row">
        <label htmlFor={`${id}-position`}>
          National IT&rsquo;s position
          <select id={`${id}-position`} value={position} onChange={(e) => setPosition(e.target.value as BtiPositionValue)}>
            {BTI_POSITION_VALUES.map((v) => (
              <option key={v} value={v}>
                {BTI_POSITION[v].label}
              </option>
            ))}
          </select>
        </label>
        <label htmlFor={`${id}-evidence`}>
          <span>
            Evidence note <span className="muted small">(optional)</span>
          </span>
          <input id={`${id}-evidence`} maxLength={400} value={evidenceNote} onChange={(e) => setEvidenceNote(e.target.value)} />
        </label>
      </div>
      <label htmlFor={`${id}-explanation`}>
        <span>
          Explanation <span className="muted small">(at least {BTI_POSITION_EXPLANATION_MIN} characters)</span>
        </span>
        <textarea id={`${id}-explanation`} rows={3} required minLength={BTI_POSITION_EXPLANATION_MIN} maxLength={2000} value={explanation} onChange={(e) => setExplanation(e.target.value)} />
      </label>
      {error && (
        <p role="alert" className="notice">
          {error}
        </p>
      )}
      <div className="actions">
        <button type="submit" className="pill" disabled={busy}>
          {busy ? 'Saving' : 'Save position'}
        </button>
        <button type="button" className="link" onClick={() => onDone(null)} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

/** Each item: BTI's verdict beside how many firms with CDW results have the control in place today, and National's position where set. */
export function BtiCompare({ measured, comments, positions, onSavePosition, showRefs = true }: {
  measured: Measured[];
  /** National IT only: what National submitted and what BTI's evaluator wrote, by item number. */
  comments?: Comments;
  /** National and Board: National's current position on the items it has updated. */
  positions?: BtiPosition[];
  /** National IT on the National view only: shows an Update button per item. */
  onSavePosition?: SavePosition;
  showRefs?: boolean;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const [shown, setShown] = useState<number | null>(null);
  const [editing, setEditing] = useState<number | null>(null);
  const m = (i: BtiItem) => measured.find((x) => x.controlId === i.controlId);
  const pos = (i: BtiItem) => positions?.find((p) => p.itemNo === i.no);
  const behind = items.filter((i) => {
    const x = m(i);
    return i.verdict === 'satisfied' && x && x.rated > 0 && x.inPlace / x.rated < 0.5;
  }).length;
  return (
    <div className="bti-compare">
      {behind > 0 && (
        <p className="bti-insight">
          <strong>{behind}</strong> {behind === 1 ? 'requirement' : 'requirements'} BTI rated satisfied {behind === 1 ? 'is' : 'are'} in place at fewer than half of the firms with CDW results today.
        </p>
      )}
      <ol className="plain bti-items">
        {items.map((i) => {
          const x = m(i);
          // In place counts fully or partly; the bar still shows the partly share on its own.
          const fully = x ? x.inPlace - x.partly : 0;
          const notYet = x ? x.rated - x.inPlace : 0;
          const w = (v: number) => `${x && x.rated ? (v / x.rated) * 100 : 0}%`;
          const c = comments?.[i.no];
          const p = pos(i);
          return (
            <li key={i.no} className="bti-item">
              <span className="bti-chips">
                <span className={`chip tone-${BTI_VERDICT[i.verdict].tone}`}>
                  <span className="sr-only">BTI&rsquo;s rating: </span>
                  {BTI_VERDICT[i.verdict].label}
                </span>
                {p && <PositionChip p={p} expanded={shown === i.no} onToggle={() => setShown(shown === i.no ? null : i.no)} />}
              </span>
              <span className="bti-item-text">
                {i.title}
                {showRefs && <span className="muted small"> {i.ref}</span>}
              </span>
              {x && x.rated > 0 ? (
                <span className="lead-gap-bar" role="img" aria-label={`${i.title}: in place at ${x.inPlace} (fully at ${fully}, partly at ${x.partly}), not yet at ${notYet}, of ${x.rated} firms with CDW results`}>
                  {fully > 0 && <span className="seg in-place-now" style={{ width: w(fully) }} />}
                  {x.partly > 0 && <span className="seg partly" style={{ width: w(x.partly) }} />}
                  {notYet > 0 && <span className="seg in-place" style={{ width: w(notYet) }} />}
                </span>
              ) : (
                <span className="muted small">No CDW results yet</span>
              )}
              <span className="small bti-now">{x && x.rated > 0 ? `In place at ${x.inPlace} of ${x.rated} firms (${fully} fully)` : ''}</span>
              <span className="bti-actions">
                {c && (c.response || c.evaluatorComment) && (
                  <button type="button" className="link bti-toggle" aria-expanded={open === i.no} onClick={() => setOpen(open === i.no ? null : i.no)}>
                    {open === i.no ? 'Hide comments' : 'Comments'}
                    <span className="sr-only"> for requirement {i.no}</span>
                  </button>
                )}
                {onSavePosition && editing !== i.no && (
                  <button type="button" className="link bti-toggle" onClick={() => setEditing(i.no)}>
                    Update<span className="sr-only"> National IT&rsquo;s position on requirement {i.no}</span>
                  </button>
                )}
              </span>
              {p && shown === i.no && editing !== i.no && <PositionDetail p={p} footnote={<>BTI&rsquo;s rating is unchanged: {BTI_VERDICT[i.verdict].label.toLowerCase()}.</>} />}
              {onSavePosition && editing === i.no && (
                <div className="bti-position">
                  <PositionForm
                    wave={1}
                    itemNo={i.no}
                    title={i.title}
                    intro={<>National IT&rsquo;s own position on this requirement today. BTI&rsquo;s rating ({BTI_VERDICT[i.verdict].label.toLowerCase()}) stays as BTI gave it.</>}
                    current={p}
                    measured={x}
                    onSave={onSavePosition}
                    onDone={(saved) => {
                      setEditing(null);
                      if (saved) setShown(i.no);
                    }}
                  />
                </div>
              )}
              {c && open === i.no && (
                <div className="bti-comments small">
                  {c.response && (
                    <p>
                      <strong>What National IT submitted:</strong> {c.response}
                    </p>
                  )}
                  <p>
                    <strong>BTI&rsquo;s evaluator:</strong> {c.evaluatorComment ?? 'No comment recorded.'}
                  </p>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <p className="legend small">
        <span className="gap-key in-place-now" aria-hidden="true" /> Fully in place today <span className="gap-key partly" aria-hidden="true" /> Partly in place <span className="gap-key in-place" aria-hidden="true" /> Not yet in place
        {positions && positions.length > 0 && <> <span className="chip bti-national legend-chip" aria-hidden="true">Met</span> National IT&rsquo;s position</>}
      </p>
    </div>
  );
}

/** "Satisfied, reported July 2025" beside "in place, measured 2026", as one card. */
export function BtiCard({ measured, comments, positions, onSavePosition, headingLevel = 3, showRefs = true, showDue = true }: {
  measured?: Measured[];
  comments?: Comments;
  positions?: BtiPosition[];
  onSavePosition?: SavePosition;
  headingLevel?: 2 | 3;
  /** CIS references beside each item: on for the National view, off on Leadership (DEC-16). */
  showRefs?: boolean;
  /** The Wave 2 due date under the summary; off where the Wave 2 card sits beside it. */
  showDue?: boolean;
}) {
  const H = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <section className="card lead-wide bti-card" aria-labelledby="bti-h">
      <H id="bti-h">How Baker Tilly International rated the network</H>
      <p className="muted small">Wave 1, submitted July 2025{measured ? ', with today’s CDW results beside it' : ''}.</p>
      <BtiSummary positions={positions} showDue={showDue} />
      {measured && (
        <details className="bti-details">
          <summary>Each Wave 1 requirement: BTI&rsquo;s rating and where firms are today</summary>
          <BtiCompare showRefs={showRefs} measured={measured} comments={comments} positions={positions} onSavePosition={onSavePosition} />
        </details>
      )}
    </section>
  );
}
